// EquipmentManager.jsx (category-free)
import MenuItem from '@mui/material/MenuItem';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, TextField, Button,
  IconButton, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Divider, LinearProgress, Snackbar, Alert, Grid, Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import {
  getFirestore, collection, doc, setDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, serverTimestamp, deleteField
} from 'firebase/firestore';

import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject, updateMetadata, listAll
} from 'firebase/storage';
import { storage } from './firebase';

/* -------------------------------------------
 * Helpers
 * ------------------------------------------- */
// Unified membership (same as Ingredients)
const ALLOWED_TYPES = ['Beverage', 'Cooking', 'Mixing', 'Prep'];

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const title = (s='') => s.slice(0,1).toUpperCase() + s.slice(1);
const toSlug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

// strip empty values
function omitEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[k] = v;
  }
  return out;
}

/* -------------------------------------------
 * Storage helpers (upload + recursive delete)
 * ------------------------------------------- */
async function uploadWithMeta(path, blobOrFile, onProgress) {
  const r = storageRef(storage, path);
  const task = uploadBytesResumable(r, blobOrFile, {
    cacheControl: 'public, max-age=31536000, immutable',
    contentType: blobOrFile?.type || 'image/jpeg'
  });
  await new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (s) => {
        if (onProgress && s.totalBytes) {
          onProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100));
        }
      },
      reject,
      resolve
    );
  });
  const url = await getDownloadURL(r);
  await updateMetadata(r, { cacheControl: 'public, max-age=31536000, immutable' }).catch(()=>{});
  return url;
}

function makeClientThumb(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const max = 512;
        const ratio = Math.min(max / img.width, max / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        }, 'image/jpeg', 0.84);
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
}

async function deleteFolder(prefixPath) {
  // Recursively delete all items under a prefix like `equipment_tools/<id>/`
  const rootRef = storageRef(storage, prefixPath);
  const walk = async (refNode) => {
    const res = await listAll(refNode);
    await Promise.all(res.items.map((it) => deleteObject(it).catch(()=>{}))); // delete files
    for (const p of res.prefixes) await walk(p); // recurse subfolders
  };
  await walk(rootRef);
}

/* -------------------------------------------
 * MultiImagePicker (gallery) with progress UI
 * ------------------------------------------- */
function MultiImagePicker({ toolId, images, setImages, onSnack, onUploadingChange }) {
  const fileRef = useRef();

  // uploadingMap: { [tempId]: { name, progress (0-100), status: 'uploading'|'done'|'error' } }
  const [uploadingMap, setUploadingMap] = useState({});

  // expose "is uploading" state to parent (to disable Save)
  const isUploading = Object.values(uploadingMap).some(u => u.status === 'uploading');
  useEffect(() => { onUploadingChange?.(isUploading); }, [isUploading, onUploadingChange]);

  const pick = () => fileRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !toolId) return;

    for (const file of files) {
      const tempId = genId();
      setUploadingMap(prev => ({
        ...prev,
        [tempId]: { name: file.name, progress: 0, status: 'uploading' }
      }));

      try {
        // 70% original, 30% thumb
        let combined = 0;
        const updateCombined = (pOrig, pThumb) => {
          combined = Math.min(100, Math.round((0.7 * (pOrig ?? 0)) + (0.3 * (pThumb ?? 0))));
          setUploadingMap(prev => ({ ...prev, [tempId]: { ...prev[tempId], progress: combined } }));
        };

        let pOrig = 0;
        const imageId = genId();
        const origUrl = await uploadWithMeta(
          `equipment_tools/${toolId}/${imageId}/original.jpg`,
          file,
          (p) => { pOrig = p; updateCombined(pOrig, 0); }
        );

        let thumbUrl = origUrl;
        let pThumb = 0;
        const thumbBlob = await makeClientThumb(file);
        if (thumbBlob) {
          const jpegThumb = new Blob([thumbBlob], { type: 'image/jpeg' });
          thumbUrl = await uploadWithMeta(
            `equipment_tools/${toolId}/${imageId}/thumb_512.jpg`,
            jpegThumb,
            (p) => { pThumb = p; updateCombined(pOrig, pThumb); }
          );
        } else {
          // no thumb; treat as 100%
          updateCombined(100, 100);
        }

        // Add to images (first image stays primary at index 0)
        setImages((prev) => [...prev, { id: imageId, fullUrl: origUrl, thumbUrl, caption: '' }]);

        // Mark done & remove from uploading list after a short delay
        setUploadingMap(prev => ({ ...prev, [tempId]: { ...prev[tempId], progress: 100, status: 'done' } }));
        setTimeout(() => {
          setUploadingMap(prev => {
            const { [tempId]: _, ...rest } = prev;
            return rest;
          });
        }, 800);

        onSnack?.('success', `Uploaded ${file.name}`);
      } catch (err) {
        console.error('Upload failed', err);
        setUploadingMap(prev => ({ ...prev, [tempId]: { ...prev[tempId], status: 'error' } }));
        onSnack?.('error', `Upload failed: ${file.name}`);
      }
    }

    if (fileRef.current) fileRef.current.value = '';
  };

  const removeImage = async (img) => {
    if (!toolId || !img?.id) return;
    try {
      await deleteFolder(`equipment_tools/${toolId}/${img.id}/`);
      setImages((prev) => prev.filter((x) => x.id !== img.id));
      onSnack?.('info', 'Image removed (not saved until you click Save).');
    } catch (e) {
      console.error(e);
      onSnack?.('error', 'Failed to remove image.');
    }
  };

  const setPrimary = (img) => {
    setImages((prev) => {
      const rest = prev.filter((x) => x.id !== img.id);
      return [img, ...rest]; // first = primary
    });
  };

  const setCaption = (imgId, val) => {
    setImages((prev) => prev.map((img) => img.id === imgId ? { ...img, caption: val } : img));
  };

  return (
    <Stack gap={1}>
      <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }} onChange={onFiles} multiple />
      <Stack direction="row" gap={1} alignItems="center">
        <Button variant="outlined" onClick={pick} disabled={!toolId}>Upload Images</Button>
        {!toolId && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Enter a Name first (used for the storage path).
          </Typography>
        )}
      </Stack>

      {/* Upload progress list */}
      {Object.keys(uploadingMap).length > 0 && (
        <Stack gap={1} sx={{ mt: 1 }}>
          {Object.entries(uploadingMap).map(([id, u]) => (
            <Box key={id} sx={{ p: 1, borderRadius: 1, border: '1px solid #eee' }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Uploading: {u.name}
              </Typography>
              <LinearProgress variant="determinate" value={u.progress ?? 0} />
            </Box>
          ))}
        </Stack>
      )}

      {/* Gallery */}
      <Grid container spacing={1}>
        {images.map((img, idx) => (
          <Grid item xs={6} sm={4} md={3} key={img.id}>
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Box position="relative">
                <img
                  src={img.thumbUrl || img.fullUrl}
                  alt="tool"
                  style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 6 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setPrimary(img)}
                  sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'white' }}
                  title="Make primary (shown in list)"
                >
                  {idx === 0 ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeImage(img)}
                  sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'white' }}
                  title="Remove"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                size="small"
                label="Caption (optional)"
                value={img.caption || ''}
                onChange={(e) => setCaption(img.id, e.target.value)}
                sx={{ mt: 1 }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {images.length === 0 && Object.keys(uploadingMap).length === 0 && (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          No images yet. Upload one or more — the first image is used as the primary.
        </Typography>
      )}
    </Stack>
  );
}

/* -------------------------------------------
 * Tool dialog (no Category)
 * ------------------------------------------- */
function ToolDialog({ open, onClose, initial }) {
  const [name, setName] = useState(initial?.name || '');
  const [types, setTypes] = useState(() => {
    const t = asArray(initial?.types);
    if (t.length) return t;
    // Back-compat: if old doc had category but no types, map it to a type suggestion.
    if (initial?.category && initial.category !== 'general') return [title(initial.category)];
    return [];
  });
  const [aliases, setAliases] = useState((initial?.aliases || []).join(', '));
  const [notes, setNotes] = useState(initial?.notes || '');
  const [cleaningNotes, setCleaningNotes] = useState(initial?.cleaning_notes || '');
  const [images, setImages] = useState(asArray(initial?.images)); // [{id, fullUrl, thumbUrl, caption?}]
  const [isUploading, setIsUploading] = useState(false);

  const [snack, setSnack] = useState({ open:false, severity:'info', message:'' });
  const openSnack = (severity, message) => setSnack({ open:true, severity, message });

  useEffect(() => {
    setName(initial?.name || '');
    setTypes(() => {
      const t = asArray(initial?.types);
      if (t.length) return t;
      if (initial?.category && initial.category !== 'general') return [title(initial.category)];
      return [];
    });
    setAliases((initial?.aliases || []).join(', '));
    setNotes(initial?.notes || '');
    setCleaningNotes(initial?.cleaning_notes || '');
    setImages(asArray(initial?.images));
    setIsUploading(false);
  }, [initial, open]);

  const id = (initial?.id || toSlug(name)) || '';

  const cleanTypes = (arr) => Array.from(new Set(
    asArray(arr).map(s => String(s || '').trim()).filter(s => ALLOWED_TYPES.includes(s))
  ));

  // ----- Types required UI state
  const typeList = asArray(types);
  const typeError = typeList.length === 0;

  const handleSave = () => {
    const aliasArr = aliases.split(',').map(a => a.trim()).filter(Boolean);
    const nextTypes = cleanTypes(types);

    if (!nextTypes.length) {
      openSnack('error', 'Please select at least one Type (Beverage, Cooking, Mixing, or Prep).');
      return;
    }

    // Back-compat: first image fills legacy single fields
    const primary = images[0];
    const payload = omitEmpty({
      id: id || toSlug(name),
      name: name.trim(),
      types: nextTypes,
      aliases: aliasArr.length ? aliasArr : undefined,
      notes: notes.trim() || undefined,
      cleaning_notes: cleaningNotes.trim() || undefined,
      images: images.length ? images : undefined,
      photo_url: primary?.fullUrl,
      photo_thumb_url: primary?.thumbUrl
    });

    onClose(payload);
  };

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="md" fullWidth>
      <DialogTitle>{initial ? 'Edit Tool' : 'Add Tool'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={e=>setName(e.target.value)} fullWidth autoFocus/>

          {/* Types (REQUIRED) */}
          <TextField
            select
            label="Types *"
            required
            value={typeList}
            onChange={(e)=>setTypes(asArray(e.target.value))}
            SelectProps={{ multiple: true, renderValue: (sel) => (sel || []).join(', ') }}
            error={typeError}
            helperText={typeError ? 'Choose at least one type' : 'Where this tool is used (Beverage/Cooking/Mixing/Prep)'}
          >
            {ALLOWED_TYPES.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>

          <TextField label="Aliases (comma-separated)" value={aliases} onChange={e=>setAliases(e.target.value)} />
          <TextField label="Notes" value={notes} onChange={e=>setNotes(e.target.value)} multiline minRows={2} />
          <TextField label="Cleaning Notes (optional)" value={cleaningNotes} onChange={e=>setCleaningNotes(e.target.value)} multiline minRows={2} />

          <Divider />
          <Typography variant="subtitle2">Images</Typography>
          <MultiImagePicker
            toolId={id}
            images={images}
            setImages={setImages}
            onSnack={openSnack}
            onUploadingChange={setIsUploading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<CloseIcon/>} onClick={()=>onClose(null)}>Cancel</Button>
        <Button
          startIcon={<CheckIcon/>}
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || typeError || isUploading}
        >
          {isUploading ? 'Uploading…' : 'Save'}
        </Button>
      </DialogActions>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open:false }))}>
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

/* -------------------------------------------
 * Manager
 * ------------------------------------------- */
export default function EquipmentManager() {
  const db = getFirestore();
  const [tools, setTools] = useState([]);
  const [qText, setQText] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [snack, setSnack] = useState({ open:false, severity:'success', message:'' });

  // Confirm states
  const [confirmArchive, setConfirmArchive] = useState({ open:false, row:null });
  const [confirmRestore, setConfirmRestore] = useState({ open:false, row:null });
  const [confirmDelete, setConfirmDelete] = useState({ open:false, row:null });

  useEffect(() => {
    const col = collection(db, 'equipment_tools');
    const q = query(col, orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTools(rows);
    });
    return () => unsub();
  }, [db]);

  const dataSource = useMemo(() => {
    const base = onlyActive ? tools.filter(t => (t.status || 'active') === 'active') : tools;
    if (!qText.trim()) return base;
    const t = qText.toLowerCase();
    return base.filter(r =>
      (r.name || '').toLowerCase().includes(t) ||
      (r.aliases || []).some(a => a.toLowerCase().includes(t)) ||
      asArray(r.types).some(tp => tp.toLowerCase().includes(t))
    );
  }, [tools, onlyActive, qText]);

  const upsert = async (payloadRaw) => {
    const payload = omitEmpty(payloadRaw);
    const now = serverTimestamp();
    await setDoc(
      doc(collection(db, 'equipment_tools'), payload.id),
      {
        ...payload,
        category: deleteField(),     // actively remove legacy field
        status: payload.status || 'active',
        updated_at: now,
        created_at: payload.created_at || now
      },
      { merge: true }
    );
    setSnack({ open:true, severity:'success', message:'Tool saved.' });
  };

  // Actions
  const doArchive = async (row) => {
    await updateDoc(doc(collection(db, 'equipment_tools'), row.id), { status: 'archived', updated_at: serverTimestamp() });
    setSnack({ open:true, severity:'info', message:'Archived.' });
  };
  const doRestore = async (row) => {
    await updateDoc(doc(collection(db, 'equipment_tools'), row.id), { status: 'active', updated_at: serverTimestamp() });
    setSnack({ open:true, severity:'success', message:'Restored.' });
  };
  const doDelete = async (row) => {
    // delete Firestore doc
    await deleteDoc(doc(collection(db, 'equipment_tools'), row.id));
    // delete ALL images under this tool’s storage prefix
    try { await deleteFolder(`equipment_tools/${row.id}/`); } catch {}
    setSnack({ open:true, severity:'warning', message:'Deleted.' });
  };

  return (
    <Box>
      <Card className="shadow rounded-2xl">
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="h5">Equipment / Tools</Typography>
              <Button startIcon={<AddIcon />} variant="contained" onClick={()=>setDialog({ open:true, initial:null })}>Add Tool</Button>
            </Stack>
          }
          subheader="Standardize tool names for Cooking, Mixing, and Prep. Add images for quick identification."
        />
        <CardContent>
          <Stack direction={{ xs:'column', md:'row' }} gap={2} alignItems={{ xs:'stretch', md:'center' }}>
            <TextField
              label="Search"
              placeholder="tea strainer, funnel, mixer…"
              value={qText}
              onChange={e=>setQText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              fullWidth
            />
            <Button variant={onlyActive? 'contained':'outlined'} onClick={()=>setOnlyActive(!onlyActive)}>
              {onlyActive? 'Showing Active' : 'Showing All'}
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack gap={1}>
            {dataSource.map(row => {
              const displayTypes = asArray(row.types);
              const primaryImg =
                row.photo_thumb_url ||
                row.photo_url ||
                (row.images?.[0]?.thumbUrl || row.images?.[0]?.fullUrl);

              return (
                <Card key={row.id} variant="outlined" className="rounded-2xl">
                  <CardContent>
                    <Stack direction={{ xs:'column', md:'row' }} justifyContent="space-between" alignItems={{ xs:'flex-start', md:'center' }} gap={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {primaryImg && (
                          <img
                            src={primaryImg}
                            alt={`${row.name} image`}
                            style={{ width:48, height:48, objectFit:'cover', borderRadius:6, marginRight:8 }}
                          />
                        )}
                        <Box>
                          <Typography variant="h6">{row.name}</Typography>
                          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                            {displayTypes.length > 0
                              ? displayTypes.map(tp => <Chip key={tp} size="small" color="primary" label={tp} />)
                              : <Chip size="small" label="No Types" />}
                            {(row.aliases||[]).map(a => <Chip key={a} size="small" label={`aka: ${a}`} />)}
                            {row.images?.length > 1 && <Chip size="small" label={`${row.images.length} images`} />}
                            {row.status === 'archived' && <Chip size="small" color="warning" label="Archived" />}
                          </Stack>
                          {(row.notes || row.cleaning_notes) && (
                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                              {row.notes && `Notes: ${row.notes}`} {row.notes && row.cleaning_notes && ' • '} {row.cleaning_notes && `Cleaning: ${row.cleaning_notes}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Stack direction="row" gap={1}>
                        <Tooltip title="Edit"><IconButton onClick={()=>setDialog({ open:true, initial: row })}><EditIcon/></IconButton></Tooltip>
                        {row.status !== 'archived' ? (
                          <Tooltip title="Archive"><IconButton onClick={()=>setConfirmArchive({ open:true, row })}><ArchiveIcon/></IconButton></Tooltip>
                        ) : (
                          <Tooltip title="Restore"><IconButton onClick={()=>setConfirmRestore({ open:true, row })}><RestoreIcon/></IconButton></Tooltip>
                        )}
                        <Tooltip title="Delete permanently"><IconButton onClick={()=>setConfirmDelete({ open:true, row })} color="error"><DeleteIcon/></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
            {dataSource.length === 0 && (
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 2 }}>No tools match your filters.</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ToolDialog
        open={dialog.open}
        initial={dialog.initial}
        onClose={async (payload) => {
          setDialog({ open:false, initial:null });
          if (!payload) return;
          await upsert(payload);
        }}
      />

      <Dialog open={confirmArchive.open} onClose={()=>setConfirmArchive({ open:false, row:null })}>
        <DialogTitle>Archive tool?</DialogTitle>
        <DialogContent>
          <Typography>This will hide <strong>{confirmArchive.row?.name}</strong> from “Active”. You can restore it later.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmArchive({ open:false, row:null })}>Cancel</Button>
          <Button color="warning" onClick={async ()=>{
            await doArchive(confirmArchive.row);
            setConfirmArchive({ open:false, row:null });
          }}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestore.open} onClose={()=>setConfirmRestore({ open:false, row:null })}>
        <DialogTitle>Restore tool?</DialogTitle>
        <DialogContent>
          <Typography>Make <strong>{confirmRestore.row?.name}</strong> active again.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmRestore({ open:false, row:null })}>Cancel</Button>
          <Button onClick={async ()=>{
            await doRestore(confirmRestore.row);
            setConfirmRestore({ open:false, row:null });
          }}>Restore</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete.open} onClose={()=>setConfirmDelete({ open:false, row:null })}>
        <DialogTitle>Delete permanently?</DialogTitle>
        <DialogContent>
          <Typography>
            This will <strong>permanently delete</strong> <em>{confirmDelete.row?.name}</em> and all of its images.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDelete({ open:false, row:null })}>Cancel</Button>
          <Button color="error" onClick={async ()=>{
            await doDelete(confirmDelete.row);
            setConfirmDelete({ open:false, row:null });
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack(s => ({ ...s, open:false }))}>
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
